import { DefaultObservableValue, Notifier, Option, safeExecute, Terminable, Terminator, UUID } from "@opendaw/lib-std";
import { PPQN } from "@opendaw/lib-dsp";
import { Propagation } from "@opendaw/lib-box";
import { NoteClipBox } from "@opendaw/studio-boxes";
import { NoteEventCollectionBoxAdapter } from "../collection/NoteEventCollectionBoxAdapter";
import { TrackBoxAdapter } from "../TrackBoxAdapter";
export class NoteClipBoxAdapter {
    type = "note-clip";
    #terminator = new Terminator();
    #context;
    #box;
    #selectedValue;
    #changeNotifier;
    #isConstructing; // Prevents stack overflow due to infinite adapter queries
    #eventCollectionSubscription = Terminable.Empty;
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
        this.#isConstructing = true;
        this.#selectedValue = this.#terminator.own(new DefaultObservableValue(false));
        this.#changeNotifier = this.#terminator.own(new Notifier());
        this.#terminator.ownAll(this.#box.pointerHub.subscribe({
            onAdded: () => this.#dispatchChange(),
            onRemoved: () => this.#dispatchChange()
        }), this.#box.subscribe(Propagation.Children, (_update) => this.#dispatchChange()), this.#box.events.catchupAndSubscribe(({ targetVertex }) => {
            this.#eventCollectionSubscription.terminate();
            this.#eventCollectionSubscription = targetVertex.match({
                none: () => Terminable.Empty,
                some: ({ box }) => this.#context.boxAdapters
                    .adapterFor(box, NoteEventCollectionBoxAdapter)
                    .subscribeChange(() => this.#dispatchChange())
            });
            this.#dispatchChange();
        }));
        this.#isConstructing = false;
    }
    catchupAndSubscribeSelected(observer) {
        return this.#selectedValue.catchupAndSubscribe(observer);
    }
    subscribeChange(observer) { return this.#changeNotifier.subscribe(observer); }
    accept(visitor) { return safeExecute(visitor.visitNoteClipBoxAdapter, this); }
    consolidate() {
        if (this.isMirrowed) {
            this.#box.events.refer(this.optCollection.unwrap().copy().box.owners);
        }
    }
    clone(consolidate) {
        const events = consolidate ? this.optCollection.unwrap().copy().box.owners : this.#box.events.targetVertex.unwrap();
        NoteClipBox.create(this.#context.boxGraph, UUID.generate(), box => {
            box.index.setValue(this.indexField.getValue());
            box.label.setValue(this.label);
            box.hue.setValue(this.hue);
            box.duration.setValue(this.duration);
            box.mute.setValue(this.mute);
            box.clips.refer(this.#box.clips.targetVertex.unwrap());
            box.events.refer(events);
        });
    }
    onSelected() { this.#selectedValue.setValue(true); }
    onDeselected() { this.#selectedValue.setValue(false); }
    get isSelected() { return this.#selectedValue.getValue(); }
    terminate() {
        this.#eventCollectionSubscription.terminate();
        this.#terminator.terminate();
    }
    get box() { return this.#box; }
    get uuid() { return this.#box.address.uuid; }
    get address() { return this.#box.address; }
    get indexField() { return this.#box.index; }
    get duration() { return this.#box.duration.getValue(); }
    get mute() { return this.#box.mute.getValue(); }
    get hue() { return this.#box.hue.getValue(); }
    get events() {
        return this.optCollection.map(collection => collection.events);
    }
    get hasCollection() { return !this.optCollection.isEmpty(); }
    get optCollection() {
        return this.#box.events.targetVertex
            .map(vertex => this.#context.boxAdapters.adapterFor(vertex.box, NoteEventCollectionBoxAdapter));
    }
    get label() { return this.#box.label.getValue(); }
    get trackBoxAdapter() {
        if (this.#isConstructing) {
            return Option.None;
        }
        return this.#box.clips.targetVertex
            .map(vertex => this.#context.boxAdapters.adapterFor(vertex.box, TrackBoxAdapter));
    }
    get isMirrowed() { return this.optCollection.mapOr(adapter => adapter.numOwners > 1, false); }
    get canMirror() { return true; }
    toString() { return `{NoteClipBoxAdapter ${UUID.toString(this.#box.address.uuid)} d: ${PPQN.toString(this.duration)}}`; }
    #dispatchChange() {
        this.#changeNotifier.notify();
        this.trackBoxAdapter.unwrapOrNull()?.clips?.dispatchChange();
    }
}
